<template>
  <div>
    <!-- Modal/Upload component starts -->
    <UploadProfileModal profileOf="user" />
    <!-- Modal/Upload component ends -->

    <!-- Modal/Successfully created component starts -->
    <SuccessModal
      :title="$t('dear_name', { name: profile.name })"
      :subtitle="$t('profile_created')"
    />
    <!-- Modal/Successfully created component ends -->
    <div class="w-318">
      <h3 class="font-bold text-24" data-cy="edit-profile-title">
        {{ $t('edit_profile') }}
      </h3>
      <div class="flex flex-row-reverse items-center justify-end mt-50">
        <div class="mx-22">
          <div class="card-subtitle">
            {{ $store.state.userInformation.name }}
          </div>
          <div class="text-sm">
            {{ $store.state.userInformation.job_position }}
          </div>
        </div>
        <div class="w-78 h-78">
          <edit-avatar
            :type="avatarType"
            :isLabelShow="!hasAvatar"
            :avatar="userAvatar"
            @click.native="openModal('upload')"
          >
            <template #text>
              <span class="text-sm">
                {{ $t('profile') }}
              </span>
            </template>
          </edit-avatar>
        </div>
      </div>
      <div class="mt-36">
        <form action="submit" @submit.prevent="saveProfileData">
          <base-input
            inputType="ease"
            id="user_name"
            v-model="profile.name"
            :inputValue="profile.name"
            width="318"
            height="50"
            :isValid="isNameValid"
            @input="nameValidation"
          >
            <template #text>
              {{ $t('name') }}
            </template>

            <template #errorMessage>
              <span v-if="isNameContainsNum">{{ $t('only_letters') }}</span>
              <span v-if="isNameTooShort">{{ $t('at_least_three_characters') }}</span>
            </template>
          </base-input>

          <phone-number-input
            @countryCode="getCountryCode"
            @isPhoneValid="phoneValidation"
            :isLogin="true"
            class="mt-20"
            :AutoFocus="false"
            v-model="profile.mobile"
            :inputValue="profile.mobile"
            :userCountryCode="profile.country_code"
          />
          <base-search-input
            class="mt-20"
            id="s_ep_jp"
            :inputPlaceHolder="$t('job_position')"
            @selectedItem="getSelectedItem"
            @searched="getJobPositions"
            :itemsToSearch="jobPositions"
            :isLoading="isLoading"
            :default="defaultJob"
            isAddable
          >
            <template #errorMessage>
              {{ $t('zone_name_required') }}
            </template>
          </base-search-input>
          <router-link :to="{ name: 'ChangePassword' }">
            <BasePasswordMeter
              class="mt-20"
              v-model="password"
              id="change_password"
              isReadonly
              :placeholder="$t('password')"
            />
          </router-link>
          <div class="mt-30 mb-50">
            <base-button
              buttonType="primary"
              radius="sm"
              type="submit"
              width="318"
              height="48"
              :disabled="isActiveProgress || isButtonDisabled"
              @click.native="saveProfileData"
              data-cy="save-setting"
            >
              <template #icon v-if="loadingButton">
                <div class="w-18 h-18">
                  <base-loading size="full" borderWidth="2" />
                </div>
              </template>
              <template v-else #text>
                {{ $t('save') }}
              </template>
            </base-button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import axiosConfig from '@/services/axios.config';
import getUser from '@/services/profile/user.service';
import PhoneNumberInput from '@/components/PhoneNumberInput.vue';
import serverErrorsHandler from '@/utils/serverErrorsHandler';
import BasePasswordMeter from '@/components/base/BasePasswordMeter.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import EditAvatar from '@/components/EditAvatar.vue';
import UploadProfileModal from '@/components/modals/UploadProfileModal.vue';
import SuccessModal from '@/components/modals/SuccessModal.vue';
import BaseSearchInput from '@/components/base/BaseSearchInput.vue';
import BaseLoading from '@/components/base/BaseLoading.vue';

export default {
  components: {
    UploadProfileModal,
    BasePasswordMeter,
    BaseInput,
    BaseButton,
    EditAvatar,
    SuccessModal,
    PhoneNumberInput,
    BaseLoading,
    BaseSearchInput,
  },
  data() {
    return {
      profile: {
        name: '',
        job_position: '',
        mobile: '',
        country_code: '',
      },
      password: '12345678',
      isActiveProgress: false,
      isNameContainsNum: false,
      isNameValid: true,
      isNameTooShort: false,
      isLoading: false,
      searchJob: '',
      jobPositions: [],
      defaultJob: {
        title: '',
      },
      isButtonDisabled: true,
    };
  },
  watch: {
    hasAvatar() {
      this.hasAvatar = !!this.userInformation.avatar;
    },
  },
  mounted() {
    this.profile = {
      name: this.userInformation.name,
      job_position: this.userInformation.job_position ?? '',
      mobile: this.userInformation.mobile,
      country_code: this.userInformation.country_code,
    };
    this.defaultJob = { title: this.userInformation.job_position ?? '' };
    this.isButtonDisabled = false;
  },
  computed: {
    ...mapGetters({ userInformation: 'getUserInformation' }),
    userAvatar() {
      return this.userInformation.avatar;
    },
    hasAvatar: {
      get() {
        return this.userInformation.avatar;
      },
      set() {
        return !!this.userInformation.avatar;
      },
    },
    avatarType() {
      return this.hasAvatar ? 'edit' : 'add';
    },
    loadingButton() {
      return this.$store.state.loading.loadingButton;
    },
  },
  methods: {
    openModal(name) {
      this.$store.dispatch('modal/open', name);
    },
    closeModal(name) {
      this.$store.dispatch('modals/close', name);
      this.labelShow = true;
    },
    getCountryCode(code) {
      this.$store.state.authentication.countryCode = code;
    },
    nameValidation() {
      const regex = /^([A-Z a-z \u0600-\u06FF])*$/;
      const isNumDetected = !regex.test(this.profile.name);
      if (this.profile.name) {
        if (isNumDetected) {
          this.isNameValid = false;
          this.isNameContainsNum = true;
          this.isButtonDisabled = true;
          this.isNameTooShort = false;
        } else {
          this.isNameTooShort = false;
          this.isNameContainsNum = false;
          this.isNameValid = true;
          this.isButtonDisabled = false;
        }
        if (this.profile.name.length <= 2) {
          this.isNameValid = false;
          this.isNameTooShort = true;
          this.isNameContainsNum = false;
          this.isButtonDisabled = true;
        }
      } else {
        this.isNameValid = true;
        this.isNameTooShort = false;
        this.isNameContainsNum = false;
        this.isButtonDisabled = false;
      }
    },
    getSelectedItem(job) {
      this.profile.job_position = job.title;
    },
    saveProfileData() {
      if (this.isNameValid && this.profile.name.length >= 2) {
        this.isActiveProgress = true;
        this.profile.mobile = this.$store.state.authentication.mobileNumber;
        this.profile.country_code = this.$store.state.authentication.countryCode;
        this.$store.commit('loading/LOADING_HANDLER', true);
        axiosConfig
          .post('/user/profile', this.profile)
          .then(() => {
            this.$toast.success(this.$t('profile_edited'));
            localStorage.removeItem('user');
            getUser();
            this.labelShow = false;
            this.isActiveProgress = false;
            this.$store.commit('loading/LOADING_HANDLER', false);
          })
          .catch((error) => {
            this.labelShow = false;
            this.isActiveProgress = false;
            this.$store.commit('loading/LOADING_HANDLER', false);
            if (error.response.status === 302) {
              this.$router.push({ name: 'ChangeNumber' });
            } else {
              serverErrorsHandler(error);
              throw error;
            }
          });
      } else {
        this.isNameValid = false;
        this.isNameTooShort = true;
      }
    },
    getJobPositions(searchedJob) {
      this.isLoading = true;
      axiosConfig
        .get(`/job-positions?per_page=10&q=${searchedJob}&columns[]=title`)
        .then((response) => {
          this.isLoading = false;
          this.jobPositions = response.data.data;
        })
        .catch((error) => {
          this.isLoading = false;
          serverErrorsHandler(error);
          throw error;
        });
    },
    phoneValidation(value) {
      this.isButtonDisabled = value;
    },
  },
};
</script>
